import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, itemCount: 0 });
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [cartAnimation, setCartAnimation] = useState(false);

  const fetchCart = async (sid) => {
    try {
      const res = await api.get('/cart', { headers: { 'x-session-id': sid }, timeout: 5000 });
      setCart(res.data);
    } catch (e) {
      // Fall back to empty cart so pages can render.
      setCart({ items: [], subtotal: 0, itemCount: 0 });
    }
  };

  useEffect(() => {
    let sid = localStorage.getItem('cart_session_id');
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem('cart_session_id', sid);
    }
    setSessionId(sid);
    fetchCart(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = async (productId, quantity = 1, specInfo = null) => {
    setLoading(true);
    try {
      const res = await api.post(
        '/cart',
        { product_id: productId, quantity, spec_info: specInfo },
        { headers: { 'x-session-id': sessionId } }
      );
      setCart(res.data);
      toast.success('已加入購物車');
      setCartAnimation(true);
      setTimeout(() => setCartAnimation(false), 800);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.error || '加入購物車失敗，請稍後再試');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    setLoading(true);
    try {
      const res = await api.put(
        `/cart/${productId}`,
        { quantity },
        { headers: { 'x-session-id': sessionId } }
      );
      setCart(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || '更新數量失敗');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    setLoading(true);
    try {
      const res = await api.delete(`/cart/${productId}`, { headers: { 'x-session-id': sessionId } });
      setCart(res.data);
      toast.info('已移除商品');
    } catch (e) {
      toast.error(e.response?.data?.error || '移除失敗');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      const res = await api.delete('/cart', { headers: { 'x-session-id': sessionId } });
      setCart(res.data);
      toast.info('已清空購物車');
    } catch (e) {
      toast.error(e.response?.data?.error || '清空購物車失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        sessionId,
        cartAnimation,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        refreshCart: () => fetchCart(sessionId),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

